(() => {
  'use strict';

  const namespace = 'SDPCartStateReconciler';
  const recoveryStorageKey = 'sdp:cart-state-reconciled:v1';
  const verificationDelayMs = 900;
  const verificationWindowMs = 6000;
  const loopGuardMs = 15000;

  const state = {
    cart: null,
    pending: null,
    verifying: false,
    started: false,
  };

  function normalizedQuantity(value) {
    const quantity = Number.parseInt(String(value), 10);
    return Number.isInteger(quantity) && quantity >= 0 ? quantity : null;
  }

  function lineFromElement(element) {
    if (!element || typeof element.closest !== 'function') return null;

    const indexed = element.closest('[data-index]');
    const dataIndex = indexed ? normalizedQuantity(indexed.dataset.index) : null;
    if (dataIndex && dataIndex > 0) return dataIndex;

    const identified = element.closest('[id^="CartItem-"], [id^="CartDrawer-Item-"]');
    if (identified) {
      const match = identified.id.match(/(?:CartItem|CartDrawer-Item)-(\d+)/);
      const idIndex = match ? normalizedQuantity(match[1]) : null;
      if (idIndex && idIndex > 0) return idIndex;
    }

    const link = element.closest('a[href*="/cart/change"]');
    if (link) {
      try {
        const url = new URL(link.href, window.location.origin);
        const urlIndex = normalizedQuantity(url.searchParams.get('line'));
        if (urlIndex && urlIndex > 0) return urlIndex;
      } catch (_error) {
        return null;
      }
    }

    return null;
  }

  function cartUrl() {
    const route = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
    return `${route.replace(/\/$/, '')}.js`;
  }

  async function readCart() {
    const response = await window.fetch(cartUrl(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart read failed with ${response.status}`);
    return response.json();
  }

  function itemForLine(cart, line) {
    if (!cart || !Array.isArray(cart.items) || !line || line < 1) return null;
    return cart.items[line - 1] || null;
  }

  function matchesExpectedState(cart, intent) {
    if (!cart || !Array.isArray(cart.items) || !intent || !intent.key) return false;
    const currentItem = cart.items.find((item) => item.key === intent.key);
    if (intent.quantity === 0) return !currentItem;
    return Boolean(currentItem && currentItem.quantity === intent.quantity);
  }

  function isNodeVisible(node) {
    if (!node || node.hidden || node.getAttribute('aria-hidden') === 'true') return false;
    if (typeof window.getComputedStyle !== 'function') return true;
    const style = window.getComputedStyle(node);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function genericCartErrorVisible() {
    const expected = window.cartStrings && window.cartStrings.error
      ? String(window.cartStrings.error).trim()
      : '';
    if (!expected) return false;

    const candidates = document.querySelectorAll(
      '#cart-errors, .cart-errors, [id*="cart-errors"], .cart-item__error-text'
    );

    return Array.from(candidates).some((node) => {
      const message = String(node.textContent || '').trim();
      return isNodeVisible(node) && message.includes(expected);
    });
  }

  function scriptConfiguration() {
    const script = document.currentScript
      || document.querySelector('script[data-sdp-cart-state-reconciler]');
    return {
      successMessage: script && script.dataset.sdpCartReconciledMessage
        ? script.dataset.sdpCartReconciledMessage
        : '',
    };
  }

  const configuration = typeof document === 'undefined'
    ? { successMessage: '' }
    : scriptConfiguration();

  function readRecoveryMarker() {
    try {
      const raw = window.sessionStorage.getItem(recoveryStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  function writeRecoveryMarker(marker) {
    try {
      window.sessionStorage.setItem(recoveryStorageKey, JSON.stringify(marker));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function clearRecoveryMarker() {
    try {
      window.sessionStorage.removeItem(recoveryStorageKey);
    } catch (_error) {
      // Storage is an enhancement only. The cart remains authoritative without it.
    }
  }

  function announceCompletedRecovery() {
    const marker = readRecoveryMarker();
    if (!marker) return;
    clearRecoveryMarker();
    if (Date.now() - marker.at > 60000 || !configuration.successMessage) return;

    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = configuration.successMessage;
    (document.querySelector('main') || document.body).appendChild(status);
  }

  function recoverFromConfirmedFalseError(intent) {
    const signature = `${intent.key}:${intent.quantity}`;
    const previous = readRecoveryMarker();
    if (previous && previous.signature === signature && Date.now() - previous.at < loopGuardMs) {
      state.pending = null;
      return;
    }

    writeRecoveryMarker({ signature, at: Date.now() });
    window.location.reload();
  }

  async function verifyPendingIntent() {
    if (state.verifying || !state.pending) return;
    state.verifying = true;

    try {
      const intent = state.pending;
      const cart = await readCart();
      state.cart = cart;

      if (matchesExpectedState(cart, intent)) {
        intent.confirmed = true;
        if (genericCartErrorVisible()) {
          recoverFromConfirmedFalseError(intent);
          return;
        }
        const remainingGrace = Math.max(0, 4000 - (Date.now() - intent.at));
        window.setTimeout(() => {
          if (state.pending === intent) state.pending = null;
        }, remainingGrace);
        return;
      }

      if (Date.now() - intent.at < verificationWindowMs) {
        window.setTimeout(verifyPendingIntent, verificationDelayMs);
      } else {
        state.pending = null;
      }
    } catch (_error) {
      // Never hide a native error or reload when the authoritative readback fails.
    } finally {
      state.verifying = false;
    }
  }

  function rememberIntent(line, quantity) {
    if (!state.cart || !line || quantity === null) return;
    const item = itemForLine(state.cart, line);
    if (!item || !item.key) return;

    state.pending = {
      key: item.key,
      line,
      quantity,
      at: Date.now(),
    };
    window.setTimeout(verifyPendingIntent, verificationDelayMs);
  }

  function captureClickIntent(event) {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest('cart-remove-button, a[href*="/cart/change"]')
      : null;
    if (!target) return;

    const line = lineFromElement(target);
    let quantity = 0;
    const link = target.matches('a[href*="/cart/change"]')
      ? target
      : target.querySelector('a[href*="/cart/change"]');

    if (link) {
      try {
        const url = new URL(link.href, window.location.origin);
        const parsed = normalizedQuantity(url.searchParams.get('quantity'));
        if (parsed !== null) quantity = parsed;
      } catch (_error) {
        quantity = 0;
      }
    }

    rememberIntent(line, quantity);
  }

  function captureQuantityIntent(event) {
    const input = event.target;
    if (!input || typeof input.matches !== 'function') return;
    if (!input.matches('input[name="updates[]"], quantity-input input')) return;
    rememberIntent(lineFromElement(input), normalizedQuantity(input.value));
  }

  async function primeCart() {
    try {
      state.cart = await readCart();
    } catch (_error) {
      state.cart = null;
    }
  }

  function start() {
    if (state.started || typeof document === 'undefined') return;
    state.started = true;
    announceCompletedRecovery();
    primeCart();

    document.addEventListener('click', captureClickIntent, true);
    document.addEventListener('change', captureQuantityIntent, true);

    const observer = new MutationObserver(() => {
      if (state.pending && genericCartErrorVisible()) verifyPendingIntent();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'aria-hidden'],
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  const api = Object.freeze({
    itemForLine,
    lineFromElement,
    matchesExpectedState,
    normalizedQuantity,
    start,
  });

  globalThis[namespace] = api;
  if (typeof document !== 'undefined') start();
})();
